import { customPayRunApiBaseUrl } from '../../shared/env';
import { apiErrorFromResponse } from '../../shared/apiErrorFromResponse';
import { createJsonClient } from '../../shared/httpClient';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import { earningDetailsFromWire, serviceLineFromWire, serviceLineToWire, warningsFromWire } from '../healthcare-rates/wireMapping';
import type { CustomPayRunGateway } from './CustomPayRunGateway';
import type {
  CalculateResponse,
  CreateCustomPayRunInput,
  CustomPayRunResponse,
  DeductionElectionWithOptOutResponse,
  DeductionOptOutInput,
  DownloadedCsv,
  DuplicateAcknowledgementInput,
  DuplicateAcknowledgementResponse,
  DuplicateFlag,
  DuplicatePreviewInput,
  DuplicatePreviewResponse,
  EarningLineInput,
  EarningLineResponse,
  EmployeeSelectionInput,
  EmployeeSelectionResponse,
  FinalizeForPaymentResponse,
  PayrollPreviewResponse,
  SubmitForReviewResponse,
} from './types';

/** A saved line's `serviceLine` is read through the one wire mapping; a line without one comes back exactly as the server sent it. */
function readLines(lines: EarningLineResponse[]): EarningLineResponse[] {
  return lines.map((l) => {
    const wire = (l as { serviceLine?: unknown }).serviceLine;
    if (wire === undefined) return l;
    const { serviceLine: _wire, ...rest } = l;
    void _wire;
    const parsed = serviceLineFromWire(wire);
    return parsed ? { ...rest, serviceLine: parsed } : rest;
  });
}

export class HttpCustomPayRunGateway implements CustomPayRunGateway {
  private readonly request = createJsonClient(customPayRunApiBaseUrl);

  create(input: CreateCustomPayRunInput): Promise<CustomPayRunResponse> {
    return this.request('/api/custom-pay-runs', { method: 'POST', body: JSON.stringify(input) });
  }

  list(signal?: AbortSignal): Promise<CustomPayRunResponse[]> {
    return this.request('/api/custom-pay-runs', undefined, signal);
  }

  getById(id: string, signal?: AbortSignal): Promise<CustomPayRunResponse> {
    return this.request(`/api/custom-pay-runs/${id}`, undefined, signal);
  }

  setMemo(id: string, memo: string | null): Promise<CustomPayRunResponse> {
    return this.request(`/api/custom-pay-runs/${id}/memo`, { method: 'PUT', body: JSON.stringify({ memo }) });
  }

  setEmployees(id: string, selections: EmployeeSelectionInput[]): Promise<EmployeeSelectionResponse[]> {
    return this.request(`/api/custom-pay-runs/${id}/employees`, { method: 'POST', body: JSON.stringify({ selections }) });
  }

  getEmployees(id: string, signal?: AbortSignal): Promise<EmployeeSelectionResponse[]> {
    return this.request(`/api/custom-pay-runs/${id}/employees`, undefined, signal);
  }

  setEarnings(id: string, employeeId: string, lines: EarningLineInput[]): Promise<EarningLineResponse[]> {
    // A line without a service line is sent exactly as before; one with a service line carries it in the wire shape (and never a rate).
    const wireLines = lines.map(({ serviceLine, ...line }) => (serviceLine ? { ...line, serviceLine: serviceLineToWire(serviceLine) } : line));
    return this.request<EarningLineResponse[]>(`/api/custom-pay-runs/${id}/earnings`, { method: 'POST', body: JSON.stringify({ employeeId, lines: wireLines }) }).then(readLines);
  }

  getEarnings(id: string, signal?: AbortSignal): Promise<EarningLineResponse[]> {
    return this.request<EarningLineResponse[]>(`/api/custom-pay-runs/${id}/earnings`, undefined, signal).then(readLines);
  }

  getDeductions(id: string, signal?: AbortSignal): Promise<DeductionElectionWithOptOutResponse[]> {
    return this.request(`/api/custom-pay-runs/${id}/deductions`, undefined, signal);
  }

  setDeductionOptOuts(id: string, employeeId: string, optOuts: DeductionOptOutInput[]): Promise<DeductionElectionWithOptOutResponse[]> {
    return this.request(`/api/custom-pay-runs/${id}/deductions/opt-outs`, { method: 'POST', body: JSON.stringify({ employeeId, optOuts }) });
  }

  calculate(id: string, idempotencyKey?: string): Promise<CalculateResponse> {
    // One key per user action: the server derives each employee's calculation key from it, so a retry or double-click reuses the same calculations.
    return this.request(`/api/custom-pay-runs/${id}/calculate`, {
      method: 'POST',
      ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    });
  }

  acknowledgeAndProceed(id: string, acknowledgeExceptions: boolean, duplicateAcknowledgements?: DuplicateAcknowledgementInput[]): Promise<CustomPayRunResponse> {
    return this.request(`/api/custom-pay-runs/${id}/acknowledge-and-proceed`, {
      method: 'POST',
      body: JSON.stringify({ acknowledgeExceptions, ...(duplicateAcknowledgements?.length ? { duplicateAcknowledgements } : {}) }),
    });
  }

  getPreview(id: string, signal?: AbortSignal): Promise<PayrollPreviewResponse> {
    return this.request<PayrollPreviewResponse>(`/api/custom-pay-runs/${id}/preview`, undefined, signal).then((preview) => ({
      ...preview,
      employees: preview.employees.map((e) => {
        const wire = e as { earningDetails?: unknown; warnings?: unknown };
        if (!Array.isArray(wire.earningDetails) && !Array.isArray(wire.warnings)) return e;
        return { ...e, earningDetails: earningDetailsFromWire(wire.earningDetails), warnings: warningsFromWire(wire.warnings) };
      }),
    }));
  }

  submitForReview(id: string, duplicateAcknowledgements?: DuplicateAcknowledgementInput[]): Promise<SubmitForReviewResponse> {
    return this.request(`/api/custom-pay-runs/${id}/submit-for-review`, {
      method: 'POST',
      ...(duplicateAcknowledgements?.length ? { body: JSON.stringify({ duplicateAcknowledgements }) } : {}),
    });
  }

  async checkDuplicates(id: string, employeeIds?: string[], signal?: AbortSignal): Promise<DuplicateFlag[]> {
    const result = await this.request<{ flags: DuplicateFlag[] }>(
      `/api/custom-pay-runs/${id}/duplicate-check`,
      { method: 'POST', body: JSON.stringify(employeeIds ? { employeeIds } : {}) },
      signal,
    );
    return result.flags;
  }

  getDuplicateAcknowledgements(id: string, signal?: AbortSignal): Promise<DuplicateAcknowledgementResponse[]> {
    return this.request(`/api/custom-pay-runs/${id}/duplicate-acknowledgements`, undefined, signal);
  }

  previewDuplicates(input: DuplicatePreviewInput, signal?: AbortSignal): Promise<DuplicatePreviewResponse> {
    return this.request('/api/custom-pay-runs/duplicate-preview', { method: 'POST', body: JSON.stringify(input) }, signal);
  }

  withdraw(id: string, reason: string): Promise<CustomPayRunResponse> {
    return this.request(`/api/custom-pay-runs/${id}/withdraw`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  discard(id: string, reason: string): Promise<CustomPayRunResponse> {
    return this.request(`/api/custom-pay-runs/${id}/discard`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  async exportCsv(id: string): Promise<DownloadedCsv> {
    const path = `/api/custom-pay-runs/${id}/export`;
    const doFetch = () => fetch(`${customPayRunApiBaseUrl}${path}`, { method: 'GET', credentials: 'include' });
    let response = await doFetch();
    if (response.status === 401 && (await refreshAccessToken())) response = await doFetch();
    if (!response.ok) throw await apiErrorFromResponse(response, `Request to ${path} failed with ${response.status}`);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const fileName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? `custom-pay-run-${id}.csv`;
    return { blob: await response.blob(), fileName };
  }

  // EPN-191: SIMULATION ONLY. Every non-2xx (not approved yet, provider not a Mock, no permission, and the 501
  // Production block) surfaces as an ApiError carrying the server's own detail; nothing is faked client-side.
  async finalizeForPayment(id: string): Promise<FinalizeForPaymentResponse> {
    return this.request(`/api/custom-pay-runs/${id}/finalize-for-payment`, { method: 'POST' });
  }
}
